import {
  Module,
  VuexModule,
  Action,
  Mutation,
  getModule
} from 'vuex-module-decorators'
import Vue from 'vue'
import axios from 'axios'

import store from '@/store'
import { User } from '@/types/User'
import { initialCookie } from '@/utils/storage-init'
import dashboardModule from '@/store/dashboard'
import companyModule from '@/store/company'
import { UserRole } from '@/utils/enums'

const name = 'auth'

@Module({
  namespaced: true,
  dynamic: true,
  name,
  store,
  preserveState: Boolean(initialCookie[name])
})
class AuthModule extends VuexModule {
  private currentUser: User | null = null
  private token?: string = ''
  private refreshToken?: string = ''

  get user() {
    return this.currentUser
  }

  get btoken() {
    return this.token || ''
  }

  get isLoggedIn() {
    return this.token !== ''
  }

  get isSuperadmin() {
    if (!this.currentUser) return false
    return this.currentUser.role === UserRole.SUPERADMIN
  }
  get isAdmin() {
    if (!this.currentUser) return false
    return this.currentUser.role === UserRole.ADMIN
  }
  get isPartner() {
    if (!this.currentUser) return false
    return this.currentUser.role === UserRole.PARTNER
  }

  get isLightPartner() {
    if (!this.currentUser) return false
    return this.currentUser.role === UserRole.LIGHTPARTNER
  }

  @Mutation
  setCurrentUser(payload: Partial<User>) {
    Vue.set(this, 'currentUser', payload)
  }

  @Mutation
  logoutUser() {
    Vue.set(this, 'currentUser', undefined)
    Vue.set(this, 'token', undefined)
    Vue.set(this, 'refreshToken', undefined)
    axios.defaults.headers['Authorization'] = `Bearer `
    localStorage.removeItem('currentWl')
  }

  @Mutation
  setTokens(payload: any) {
    Vue.set(this, 'token', payload.token)
    Vue.set(this, 'refreshToken', payload.refreshToken)
    axios.defaults.headers['Authorization'] = `Bearer ${payload.token}`
  }

  @Action({ rawError: true })
  async fetchUserOnce(): Promise<User> {
    const { data } = await axios.get(
      `/admin/${this.user!.whitelabel.slug || this.user!.whitelabel}/user`
    )
    return data
  }

  @Action({ rawError: true })
  async login(payload: {
    email: string
    password: string
    whitelabel: string
  }) {
    try {
      const { data } = await axios.post(
        `/admin/${payload.whitelabel}/authenticate`,
        payload
      )

      if (data.company) {
        Object.assign(data.user, { company: data.company[0] })
      }
      this.context.commit('setCurrentUser', data.user)
      this.context.commit('setTokens', {
        token: data.token,
        refreshToken: data.refreshToken
      })
      if (data.company && data.company.length) {
        companyModule.setCompany(data.company[0])
        companyModule.setCompanies(data.company)
      }
    } catch (error) {
      dashboardModule.newError('Login fehlgeschlagen. Bitte versuche es erneut')
    }
  }

  @Action({ rawError: true })
  async logout() {
    try {
      this.context.commit('logoutUser')
    } catch (error) {
      dashboardModule.newError(
        'Logout fehlgeschlagen. Bitte versuche es erneut'
      )
    }
  }

  @Action({ rawError: true })
  async updateProfile(payload: { whitelabel: string; user: Partial<User> }) {
    try {
      const { data } = await axios.put(
        `/admin/${payload.whitelabel}/users`,
        payload.user
      )
      await companyModule.fetchAllPartnerCompanies()
      if (
        data.user.role === UserRole.PARTNER ||
        data.user.role === UserRole.LIGHTPARTNER
      ) {
        if (companyModule.company) {
          Object.assign(data.user, { company: companyModule.company })
        } else {
          Object.assign(data.user, { company: companyModule.companies[0] })
        }
      }
      this.context.commit('setCurrentUser', data.user)
      dashboardModule.newMessage('Profil wurde erfolgreich aktualisiert')
    } catch (error) {
      dashboardModule.newError('Profil konnte nicht aktualisiert werden')
    }
  }

  @Action({ rawError: true })
  async updatePassword(payload: {
    whitelabel: string
    password: { oldPassword: string; newPassword: string }
  }) {
    try {
      const { data } = await axios.put(
        `/admin/${payload.whitelabel}/users/changepassword`,
        payload.password
      )
      this.context.commit('setTokens', {
        token: data.token,
        refreshToken: data.refreshToken
      })
      dashboardModule.newMessage('Passwort wurde erfolgreich aktualisiert')
    } catch (error) {
      dashboardModule.newError('Passwort konnte nicht aktualisiert werden')
    }
  }
}

export default getModule(AuthModule)
